/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo } from 'react';

import { shallowEqualObjects } from 'shallow-equal';

import { jsx } from '@atlaskit/css';
import { useConfluenceEntities } from '@atlassian/conversation-assistant-service/services/confluence/useConfluenceEntities';

import { BaseConfluencePageSelectorInner, extractContentId } from '../base-page-selector';
import type { ConfluencePageSelectorProps } from '../base-page-selector/types';

export const FOR_TESTING_extractContentId: (ari: string) => string = extractContentId;
const HOOK_OUTPUT_PROPS = ['listEntities'] as const;

const omitHookOutputProps = <T extends Record<string, unknown>>(
	props: T,
	keys: readonly string[],
): Record<string, unknown> =>
	Object.fromEntries(Object.entries(props).filter(([key]) => !keys.includes(key)));

/**
 * Memoized version of BaseConfluencePageSelector with custom comparison function.
 */
const BaseConfluencePageSelector = memo(BaseConfluencePageSelectorInner, (prevProps, nextProps) =>
	shallowEqualObjects(
		omitHookOutputProps(prevProps, HOOK_OUTPUT_PROPS),
		omitHookOutputProps(nextProps, HOOK_OUTPUT_PROPS),
	),
);

/**
 * AFM consumers should use this web-specific component
 */
export const ConfluencePageSelector = ({
	cloudId,
	spaceId,
	contentTypes,
	...props
}: ConfluencePageSelectorProps): JSX.Element => {
	const { listEntities } = useConfluenceEntities(cloudId);
	return (
		<BaseConfluencePageSelector
			spaceId={spaceId}
			contentTypes={contentTypes}
			listEntities={listEntities}
			{...props}
		/>
	);
};
